import {NgModule} from '@angular/core';
import {PreloadAllModules, RouterModule, Routes} from '@angular/router';
import {AuthGuard} from './auth/auth.guard';
import {BetriebsauswahlPageComponent} from './modules/pages/betriebsauswahl/betriebsauswahl.page.component';
import {AppNavigationComponent} from './modules/pages/app-navigation/app-navigation.component';
import {NavigationLink} from './modules/pages/app-navigation/navigation-link.interface';
import {NavigationButton} from './modules/pages/app-navigation/navigation-button.interface';
import {NavButtonIds} from './modules/pages/app-navigation/nav-button-ids';
import {ButtonVariant, PdfViewerRoute, RouterActions} from '@adnova/jf-ng-components';
import {ProduktDialogActions} from './store/actions/produkt-dialog.actions';
import {KundeDialogActions} from './store/actions/kunde-dialog.actions';
import {FakturierungsbelegEntitiesActions} from './store/actions/fakturierungsbeleg-entities.actions';
import {Belegart} from './util/belegart.enum';


/**
 * Die BetriebsauswahlPage muss über der Ebene der Routes liegen,
 * in der Sie eingebunden sein soll, um nicht bei jedem Navigationsschritt neu geladen zu werden.
 *
 */

const routes: Routes = [
  {
    /*
    * INFO: Alle Routen, die unterhalb dieses Eintrags liegen, verwenden die Betriebsauswahl.
    * Da dafür die Inhaber geladen werden müssen, werden sie außerdem durch den AuthGuard geschützt.
    * Alle Seiten, die die Betriebsauswahl anzeigen, sollen außerdem den NavigationService verwenden und potenziell
    * an der Betriebssynchronisation teilnehmen.
    * Diese Einstellungen können pro Child-Route individuell überschrieben werden.
    *
    */
    path: '',
    component: BetriebsauswahlPageComponent,
    canActivate: [AuthGuard],
    data: {
      redirectAllowed: true, // INFO: Automatische Betriebssynchronisation aktivieren.
      useInhaberService: true, // INFO: NavigationService verwenden. Inhaber werden geladen und Fallback-ID wird automatisch gesetzt.
      navigationLinks: [ // INFO: Navigationselemente für die App-Navigation oberhalb des Contents.
        {
          label: 'Belege',
          iconName: 'fileDoc',
          route: '/fakturierungsbelege',
        },
        {
          label: 'Kontakte',
          iconName: 'user',
          route: '/kunden',
        },
        {
          label: 'Produkte',
          iconName: 'stack',
          route: '/produkte',
        },
      ] as NavigationLink[],
    },
    children: [
      {
        /*
        * INFO: Alle Routen, die unterhalb dieses Eintrags liegen, verwenden die App-Navigation.
        * Dabei handelt es sich um eine Navigationsleiste, die sich oberhalb des Inhalts befindet.
        */
        path: '',
        component: AppNavigationComponent,
        children: [
          {
            path: '',
            pathMatch: 'prefix',
            redirectTo: 'fakturierungsbelege/inhaber/',
          },
          {
            path: 'fakturierungsbelege',
            children: [
              {
                path: 'inhaber',
                children: [
                  {
                    path: ':inhaber',
                    children: [
                      {
                        path: '',
                        loadComponent: () => import('./modules/routes/fakturierungsbelege/fakturierungsbelege.component').then(m => m.FakturierungsbelegeComponent),
                        data: {
                          buttons: [
                            {
                              id: NavButtonIds.createRechnung,
                              label: 'Neue Rechnung',
                              action: FakturierungsbelegEntitiesActions.createFakturierungsbelegWithCurrentBetriebId({redirectToForm: true, belegart: Belegart.Rechnung}),
                              variant: 'primary' as ButtonVariant,
                              icon: 'plus',
                              menuItems: [
                                {
                                  id: NavButtonIds.createLieferschein,
                                  label: 'Neuer Lieferschein',
                                  action: FakturierungsbelegEntitiesActions.createFakturierungsbelegWithCurrentBetriebId({redirectToForm: true, belegart: Belegart.Lieferschein}),
                                  icon: 'plus',
                                },
                                {
                                  id: NavButtonIds.createAngebot,
                                  label: 'Neues Angebot',
                                  action: FakturierungsbelegEntitiesActions.createFakturierungsbelegWithCurrentBetriebId({redirectToForm: true, belegart: Belegart.Angebot}),
                                  icon: 'plus',
                                },
                                {
                                  id: NavButtonIds.createGutschrift,
                                  label: 'Neue Gutschrift',
                                  action: FakturierungsbelegEntitiesActions.createFakturierungsbelegWithCurrentBetriebId({redirectToForm: true, belegart: Belegart.Gutschrift}),
                                  icon: 'plus',
                                },
                              ],
                            },
                          ] as NavigationButton[],
                        },
                      },
                      {
                        path: 'edit/fakturierungsbeleg',
                        data: {
                          redirectAllowed: false, // INFO: Deaktivieren der automatischen Betriebssynchronisation.
                          navigationLinks: [] as NavigationLink[], // INFO: Oben keine Navigationselemente anzeigen.
                          buttons: [
                            {
                              id: 'back-to-overview',
                              label: 'Zur Übersicht',
                              action: RouterActions.navigateByUrl({url: '/fakturierungsbelege/inhaber/:inhaber'}),
                              variant: 'ghost' as ButtonVariant,
                              icon: 'arrowLeft',
                            }
                          ] as NavigationButton[],
                        },
                        loadComponent: () => import('./modules/routes/fakturierungsbelege-edit/fakturierungsbelege-edit.component').then(c => c.FakturierungsbelegeEditComponent),
                      },
                      {
                        path: 'edit/fakturierungsbeleg/:fakturierungsbeleg',
                        data: {
                          redirectAllowed: false, // INFO: Deaktivieren der automatischen Betriebssynchronisation.
                          navigationLinks: [] as NavigationLink[], // INFO: Oben keine Navigationselemente anzeigen.
                          buttons: [
                            {
                              id: 'back-to-overview',
                              label: 'Zur Übersicht',
                              action: RouterActions.navigateByUrl({url: '/fakturierungsbelege/inhaber/:inhaber'}),
                              variant: 'ghost' as ButtonVariant,
                              icon: 'arrowLeft',
                            }
                          ] as NavigationButton[],
                        },
                        loadComponent: () => import('./modules/routes/fakturierungsbelege-edit/fakturierungsbelege-edit.component').then(c => c.FakturierungsbelegeEditComponent),
                      },
                      {
                        path: 'edit/preview/fakturierungsbeleg/:fakturierungsbeleg',
                        data: {
                          redirectAllowed: false, // INFO: Deaktivieren der automatischen Betriebssynchronisation.
                          navigationLinks: [] as NavigationLink[], // INFO: Oben keine Navigationselemente anzeigen.
                          buttons: [
                            {
                              id: 'back-to-draft',
                              label: 'Zum Entwurf',
                              action: RouterActions.navigateByUrl({url: '/fakturierungsbelege/inhaber/:inhaber/edit/fakturierungsbeleg/:fakturierungsbeleg'}),
                              variant: 'ghost' as ButtonVariant,
                              icon: 'arrowLeft',
                            }
                          ] as NavigationButton[],
                        },
                        loadComponent: () => import('./modules/routes/fakturierungsbelege-preview/fakturierungsbelege-preview.component').then(c => c.FakturierungsbelegePreviewComponent),
                      },
                      {
                        path: 'preview/fakturierungsbeleg/:fakturierungsbeleg',
                        data: {
                          redirectAllowed: false, // INFO: Deaktivieren der automatischen Betriebssynchronisation.
                          navigationLinks: [] as NavigationLink[], // INFO: Oben keine Navigationselemente anzeigen.
                          buttons: [
                            {
                              id: 'back-to-overview',
                              label: 'Zur Übersicht',
                              action: RouterActions.navigateByUrl({url: '/fakturierungsbelege/inhaber/:inhaber'}),
                              variant: 'ghost' as ButtonVariant,
                              icon: 'arrowLeft',
                            }
                          ] as NavigationButton[],
                        },
                        loadComponent: () => import('./modules/routes/fakturierungsbelege-preview/fakturierungsbelege-preview.component').then(c => c.FakturierungsbelegePreviewComponent),
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'kunden',
            children: [
              {
                path: 'inhaber',
                children: [
                  {
                    path: ':inhaber',
                    children: [
                      {
                        path: '',
                        loadComponent: () => import('./modules/routes/kunde/kunde.component').then(m => m.KundeComponent),
                        data: {
                          buttons: [
                            {
                              id: 'create-contact',
                              label: 'Neuer Kontakt',
                              action: KundeDialogActions.open({kunde: undefined}),
                              variant: 'primary' as ButtonVariant,
                            }
                          ] as NavigationButton[],
                        },
                      },
                    ]
                  },
                ],
              },
            ],
          },
          {
            path: 'produkte',
            children: [
              {
                path: 'inhaber',
                children: [
                  {
                    path: ':inhaber',
                    children: [
                      {
                        path: '',
                        loadComponent: () => import('./modules/routes/produkte/produkte.component').then(m => m.ProdukteComponent),
                        data: {
                          buttons: [
                            {
                              id: 'create-product',
                              label: 'Neues Produkt',
                              action: ProduktDialogActions.open({}),
                              variant: 'primary' as ButtonVariant,
                            }
                          ] as NavigationButton[],
                        },
                      },
                    ]
                  },
                ],
              },
            ],
          },
        ],
      },
    ]
  },
  {
    path: '',
    component: AppNavigationComponent,
    children: [
      {
        path: 'legal/impressum',
        data: {
          redirectAllowed: false, // INFO: Deaktivieren der automatischen Betriebssynchronisation.
          navigationLinks: [] as NavigationLink[], // INFO: Oben keine Navigationselemente anzeigen.
          buttons: [
            {
              id: 'back-to-overview',
              label: 'Zur Übersicht',
              action: RouterActions.navigateByUrl({url: '/fakturierungsbelege/inhaber/:inhaber'}),
              variant: 'ghost' as ButtonVariant,
              icon: 'arrowLeft',
            }
          ] as NavigationButton[],
        },
        loadComponent: () => import('./modules/routes/legal/impressum/impressum.component').then(m => m.ImpressumComponent),
      },
    ]
  },
  PdfViewerRoute,
  {
    path: '**',
    redirectTo: '/fakturierungsbelege'
  },
];

@NgModule({
  imports: [RouterModule.forRoot(
    routes,
    {
      scrollPositionRestoration: 'top',
      preloadingStrategy: PreloadAllModules
    }
  )],
  providers: [],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
